import { DetectedEvent } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useEventSeverity } from './useEventSeverity';
import { capitalize, cn, isNonEmpty } from '@ais-ui/utils';
import React, { useMemo } from 'react';

import { EventGridItem } from './EventGridItem';
import {
  SentMessageWithDate,
  useFetchAllEventdetectorDefinitions,
  useMessagesSentList,
} from '@ais-ui/api';
import { PiCheck, PiChecks, PiHourglass } from 'react-icons/pi';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ais-ui/ui-core';

interface EventDetailsItemProps {
  event: DetectedEvent;
}

interface NotificationSentIconProps {
  notificationsDisabled: boolean;
  sentMessage: SentMessageWithDate | null;
}

const NotificationSentIcon = ({
  notificationsDisabled,
  sentMessage,
}: NotificationSentIconProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const { icon, tooltipText } = useMemo(() => {
    if (!sentMessage) {
      return {
        icon: <PiHourglass className="h-6 w-6" />,
        tooltipText: messages.eventsNoNotificationSent,
      };
    }

    if (sentMessage.acknowledged) {
      return {
        icon: <PiChecks className="h-6 w-6 text-green-500" />,
        tooltipText: sentMessage.time
          ? `Notification sent at ${formatDateTimeWithTimeZone(
              sentMessage.time
            )} and acknowledged`
          : 'Notification acknowledged',
      };
    }

    return {
      icon: <PiCheck className="h-6 w-6 text-green-500" />,
      tooltipText: sentMessage.time
        ? `Notification sent at ${formatDateTimeWithTimeZone(sentMessage.time)}`
        : 'Notification sent',
    };
  }, [sentMessage, formatDateTimeWithTimeZone, messages]);

  if (notificationsDisabled) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger>{icon}</TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
};

export const EventDetailsItem = ({ event }: EventDetailsItemProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const severity = useEventSeverity(event);

  const { data: detectors, isLoading: isLoadingDetectors } =
    useFetchAllEventdetectorDefinitions();
  const now = useMemo(() => new Date(), []);

  const { data: sentMessages, isLoading: isLoadingSentMessages } =
    useMessagesSentList(event.activated, now);
  const matchingDetector = useMemo(() => {
    if (!detectors || !detectors?.records || !event.name) return null;

    return (
      detectors.records.find((detector) => detector.name === event.name) || null
    );
  }, [detectors, event.name]);

  const sentMessage = useMemo(() => {
    if (!sentMessages || !sentMessages?.records) return null;

    return (
      sentMessages.records.find((message) => message.eventId === event.id) ||
      null
    );
  }, [sentMessages, event.id]);

  return (
    <div className="flex flex-col space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium flex-none">
            {messages.eventsItemSeverityLabel}
          </p>
          <div className="flex items-center space-x-1.5">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                severity === 'note' && 'bg-blue-500',
                severity === 'warning' && 'bg-yellow-500',
                severity === 'alert' && 'bg-red-500'
              )}
            ></div>
            <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
              {isNonEmpty(capitalize(severity))
                ? capitalize(severity)
                : 'Unknown'}
            </p>
          </div>
        </div>
        <EventGridItem
          label={messages.eventsItemDetectorLabel}
          value={event.name}
        />
      </div>
      <EventGridItem
        label={messages.eventsItemActivatedLabel}
        value={formatDateTimeWithTimeZone(event.activated)}
      />
      <EventGridItem
        label={messages.eventsItemDeactivatedLabel}
        value={
          formatDateTimeWithTimeZone(event.deactivated)
            ? formatDateTimeWithTimeZone(event.deactivated)
            : messages.eventsItemDeactivatedFalseValue
        }
      />
      <EventGridItem
        label={messages.eventsItemSubtypeLabel}
        value={event.subtype ? event.subtype : event.type}
      />
      <div className="flex justify-between items-center">
        <EventGridItem
          label={messages.eventsItemSendNotificationLabel}
          value={
            matchingDetector?.sendShipNotification
              ? 'Notifications are enabled'
              : 'Notifications are disabled'
          }
        />
        <NotificationSentIcon
          notificationsDisabled={!matchingDetector?.sendShipNotification}
          sentMessage={sentMessage}
        ></NotificationSentIcon>
      </div>
      <EventGridItem
        label={messages.eventsItemTextLabel}
        value={event.actText}
      />
    </div>
  );
};
